import { useState, useCallback } from "react";
import { Upload, FileCode, FileText, Database } from "lucide-react";

export default function FileDropZone({ onFilesSelected, isUploading }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(Array.from(e.dataTransfer.files));
    }
  }, [onFilesSelected]);

  const handleFileInput = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(Array.from(e.target.files));
    }
  }, [onFilesSelected]);

  return (
    <div
      className={`relative border-2 border-dashed rounded-sm p-8 transition-colors duration-200 cursor-pointer ${
        isDragging
          ? "drop-zone-active border-neon-cyan bg-neon-cyan/5"
          : "border-[#2A2F3A] hover:border-[#4A5568] bg-surface-200/30"
      } ${isUploading ? "opacity-60 pointer-events-none" : ""}`}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !isUploading && document.getElementById('file-input').click()}
      data-testid="file-drop-zone"
    >
      <input
        id="file-input"
        type="file"
        className="hidden"
        multiple
        accept=".sdlppx,.sdlxliff,.sdltm,.sdltb,.xliff,.xlf"
        onChange={handleFileInput}
        data-testid="file-input"
      />

      <div className="flex flex-col items-center gap-4">
        <div className={`p-4 rounded-sm ${isDragging ? "bg-neon-cyan/10" : "bg-surface-300"}`}>
          <Upload
            className={`w-8 h-8 ${isDragging ? "text-neon-cyan" : "text-[#64748B]"}`}
            strokeWidth={1.5}
          />
        </div>

        <div className="text-center">
          <p className="text-[#E0E0E0] font-ui font-medium mb-1">
            {isUploading ? "Importing project files..." : "Drop your Trados files here"}
          </p>
          <p className="text-[#64748B] text-sm font-ui">
            or click to browse
          </p>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <FileTag icon={<FileCode className="w-3.5 h-3.5" strokeWidth={1.5} />} label=".sdlppx" />
          <FileTag icon={<FileText className="w-3.5 h-3.5" strokeWidth={1.5} />} label=".sdlxliff" />
          <FileTag icon={<Database className="w-3.5 h-3.5" strokeWidth={1.5} />} label=".sdltm" />
          <FileTag icon={<Database className="w-3.5 h-3.5" strokeWidth={1.5} />} label=".sdltb" />
        </div>
      </div>

      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#050505]/60 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
            <span className="text-neon-cyan font-ui text-sm">Processing files...</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FileTag({ icon, label }) {
  return (
    <span className="flex items-center gap-1.5 text-[#64748B] text-xs font-mono px-2 py-1 bg-surface-300/50 rounded-sm border border-[#2A2F3A]">
      {icon}
      {label}
    </span>
  );
}
